import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import SITE_CONFIG from '../config/siteConfig';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '685300429300-e4reqvucu2d9l8faqs3conkq9ign8u73.apps.googleusercontent.com';

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const googleButtonRef = useRef(null);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Initialize Google Sign-In
  useEffect(() => {
    // Wait for Google script to load
    const initializeGoogle = () => {
      if (!window.google || !googleButtonRef.current) return;

      const handleGoogleResponse = async (response) => {
        setLoading(true);
        setError('');

        try {
          const result = await authService.googleLogin(response.credential);
          
          if (result.access && result.user) {
            localStorage.setItem('access_token', result.access);
            localStorage.setItem('refresh_token', result.refresh);
            // Force page reload to update auth state
            window.location.href = from;
          } else {
            setError(result.error || 'Google sign-in failed. Please try again.');
          }
        } catch (err) {
          console.error('Google sign-in error:', err);
          setError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          { 
            theme: 'outline', 
            size: 'large',
            width: 300,
            text: 'signin_with',
            shape: 'rectangular',
          }
        );
      } catch (err) {
        console.error('Failed to initialize Google Sign-In:', err);
      }
    };

    // Check if Google script is already loaded
    if (window.google) {
      initializeGoogle();
    } else {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogle();
        }
      }, 100);

      // Cleanup interval after 5 seconds
      setTimeout(() => clearInterval(checkGoogle), 5000);
    }
  }, [from]);

  return (
    <>
      <Helmet>
        <title>Login - AI Carousel Maker</title>
        <link rel="canonical" href={`${SITE_CONFIG.siteUrl}/login`} />
        <meta name="description" content="Sign in to AI Carousel Maker. Access your account to create stunning AI-powered carousel posts for social media." />
        <meta property="og:title" content="Login - AI Carousel Maker" />
        <meta property="og:url" content={`${SITE_CONFIG.siteUrl}/login`} />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <img 
              src="/logo.png" 
              alt="NovialCraft" 
              className="h-12 sm:h-16 w-auto"
            />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Welcome to NovialCraft
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to create stunning carousels with AI
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {loading && (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Signing in...</span>
            </div>
          )}

          {!loading && (
            <div className="space-y-4">
              {/* Google Sign-In Button */}
              <div className="flex justify-center">
                <div ref={googleButtonRef}></div>
              </div>

              {/* Apple Sign-In Placeholder */}
              <div className="flex justify-center">
                <button
                  disabled
                  className="w-[300px] flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-md bg-black text-white font-medium opacity-50 cursor-not-allowed"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  Sign in with Apple (Coming Soon)
                </button>
              </div>
            </div>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">
                Secure authentication
              </span>
            </div>
          </div>

          <div className="text-center text-sm text-gray-500">
            <p>
              By signing in, you agree to our{' '}
              <a href="/terms-and-conditions" className="text-primary-600 hover:text-primary-500">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy-policy" className="text-primary-600 hover:text-primary-500">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default LoginPage;
